import { ACMEErrorResponse, Address, LooseAutocomplete } from '@b2b/types/core';

export interface PaymentData {
  type?: LooseAutocomplete<'CreditCard' | 'Voucher' | 'Check' | 'Cash'>;
  contactFirstName: string;
  contactLastName: string;
  phoneNumber: string;
  address: Address;
  ticketDelivery: string;
  chargeAmount: string | null;
  hasSubscription: boolean;
  firstName: string;
  lastName: string;

  acmeToken?: string | null;
  ccLastFourDigits?: string | null;
  creditCardBrand?: string | null;
  cvc?: string | null;
  expDate?: string | null;
  manualEntryCardNumber?: string | null;
}

export interface SecureCheckoutConfig {
  mid: string;
  urls: {
    b2bApi: {
      value: string;
      publishableKey: string;
    };
    paymentsApi: {
      value: string;
      publishableKey: string;
    };
    checkout: {
      value: string;
    };
  };
  environment: string;
}

export interface SecureCheckoutOptionsProps {
  formattedBillingAddress: string;
  modal: {
    title: string;
    close: string;
    submit: string;
  };
  authorizePayment: {
    paymentData: {
      externalId: string;
      chargeAmount: string;
      firstName: string;
      lastName: string;
      creditCardVerificationData: Partial<{
        address1: string;
        address2: string;
        country: string;
        zipCode: string;
        city: string;
        state: string;
      }>;
    };
    saleChannel: string;
  };
  capturePayment: {
    loaderHtml: string;
  };
  autorenew: {
    enabled: boolean;
    html: string;
    url: string;
  };
  terms: {
    html: string;
    url: string;
  };
  creditCardSettings: {
    supportedCardsText: string;
    showVisaLogo: boolean;
    showMcLogo: boolean;
    showDiscoverLogo: boolean;
    showAmexLogo: boolean;
  };
  variables: Partial<{
    '--acme-font-family': string;
    '--acme-primary-color': string;
    '--acme-error-color': string;
    '--acme-background-color': string;
    '--acme-color': string;
    '--acme-link-color': string;
    '--acme-link-hover-color': string;
    '--acme-dialog-width': string;
    '--acme-dialog-content-height': string;
    '--acme-dialog-header-background-color': string;
    '--acme-dialog-header-color': string;
    '--acme-dialog-header-font-size': string;
    '--acme-dialog-button-primary-background-color': string;
    '--acme-dialog-button-primary-border-color': string;
    '--acme-dialog-button-primary-color': string;
    '--acme-dialog-button-primary-font-size': string;
    '--acme-dialog-button-primary-text-transform': string;
    '--acme-dialog-button-text-background-color': string;
    '--acme-dialog-button-text-border-color': string;
    '--acme-dialog-button-text-color': string;
    '--acme-dialog-button-text-font-size': string;
    '--acme-dialog-button-text-text-transform': string;
  }>;
}

export type ACMECORSCheckoutApiError = {
  type: 'api.error';
  params: ACMEErrorResponse | { type: 'NETWORK_ERROR' } | { type: 'TIMEOUT_ERROR' };
};

export type ACMECORSCheckoutError =
  | ACMECORSCheckoutApiError
  | { type: 'sdk.render_failed'; params: { message: string } }
  | { type: 'sdk.cancel_flow' }
  | { type: 'sdk.billing_address.request_change' };

export function isACMECORSCheckoutError(value: unknown): value is ACMECORSCheckoutError {
  const sdkErrorTypes = [
    'api.error',
    'sdk.render_failed',
    'sdk.cancel_flow',
    'sdk.billing_address.request_change',
  ] as const;

  const type = (value as ACMECORSCheckoutError)?.type;

  return sdkErrorTypes.includes(type);
}
